import isEmpty from 'lodash/isEmpty';
import { isSuperadmin } from 'shared/utils';
import moment from 'moment';
import { turnsActions } from 'core/Turns';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    ExpandButton,
    IconButton,
    SelectSearch
} from 'mol-ui';
import {
    CalendarContainer,
    Col,
    Container,
    DoctorMessage,
    FilterCol,
    FilterContainer,
    Filters,
    OnlyXS,
    Row
} from './styled';
import {
    CalendarWrapper,
    TurnDownloadModal,
    TurnSlider
} from './components';
import {
    DoctorPicker,
    OfficePicker,
    PatientPicker,
    SpecialtyPicker
} from 'views/pickers';
import {
    ErrorMessage,
    ExternalTurnModal
} from 'views/components';
import React, { useCallback, useEffect, useState } from 'react';
import {
    useDispatch,
    useErrors,
    useInput,
    useObjectState,
    useSelector,
    useToggle,
    useWindowSize
} from 'hooks';

const TurnListing = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { xs } = useWindowSize();
    const { user: { email } } = useSelector('account');
    const {
        availableAgenda,
        availableAgendaLoading,
        eventTurns: turns,
        listLoading
    } = useSelector('turns');
    const loading = availableAgendaLoading || listLoading;
    const [doctorInput, doctor] = useInput({}, {}, newDoctor => {
        clearError('doctor');
        if (!!selectedOffice?.doctor) {
            setSelectedOffice({});
        }
        if (!isEmpty(office) && office?.doctor?.doctor_id !== newDoctor?.id) {
            clearError('office');
            setOffice({});
        }
    });
    const [downloadActive, setDownloadActive] = useState(false);
    const [externalTurnModal, setExternalTurnModal] = useState(false);
    const [forceStepInput, forceStep] = useInput(undefined);
    const [modal, setModal] = useState('');
    const [newSearch, setNewSearch] = useState(false);
    const [officeInput, office, setOffice] = useInput({}, {}, () => clearError('office'));
    const [patientInput, patient] = useInput({}, {}, () => clearError('patient'));
    const { clearError, errors: searchErrors, setErrors: setSearchErrors } = useErrors();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedDoctor, setSelectedDoctor] = useState({});
    const [selectedOffice, setSelectedOffice] = useState({});
    const [selectedPatient, setSelectedPatient] = useState({});
    const [selectedRange, setSelectedRange] = useObjectState({
        end: moment().endOf('month').format('YYYY-MM-DD'),
        start: moment().startOf('month').format('YYYY-MM-DD')
    });
    const { end, start } = selectedRange;
    const [selectedSpecialty, setSelectedSpecialty] = useState({});
    const [selectedTurn, setSelectedTurn] = useState(null);
    const [showFilters, toggleFilters] = useToggle(true);
    const [specialtyInput, specialty] = useInput(null, {}, () => {
        clearError('doctor');
        clearError('specialty');
    });

    const validateSearch = () => {
        const { id: doctor_id } = doctor ?? {};
        const { id: office_id } = office ?? {};
        const { id: specialty_id } = specialty ?? {};
        const searchErrors = {
            ...(!doctor_id && !specialty_id && { doctor: t('Requerido'), specialty: t('Requerido') }),
            ...(!office_id && { office: t('Requerido') })
        };
        setSearchErrors(searchErrors);
        return (isEmpty(searchErrors));
    };

    const handleSearch = useCallback(() => {
        const { id: doctor_id } = selectedDoctor ?? {};
        const { id: office_id } = selectedOffice ?? {};
        const { id: patient_id } = selectedPatient ?? {};
        const { id: specialty_id } = selectedSpecialty ?? {};
        const { end: end_date, start: start_date } = selectedRange ?? {};

        if (validateSearch()) {
            const filters = {
                doctor_id,
                end_date,
                office_id,
                ...(patient_id && { patient_id }),
                specialty_id,
                start_date
            };
            dispatch(turnsActions.setTurnsSelectedDoctorId(doctor_id));
            dispatch(turnsActions.setTurnsSelectedSpecialtyId(specialty_id));
            dispatch(turnsActions.setTurnsSelectedOfficeId(office_id));
            dispatch(turnsActions.getAvailableAgenda(filters));
        }
    }, [
        selectedDoctor,
        selectedOffice,
        selectedPatient,
        selectedRange,
        selectedSpecialty,
        validateSearch
    ]);

    useEffect(() => {
        if (newSearch) {
            setNewSearch(false);
            handleSearch();
        }
    }, [handleSearch, newSearch]);

    const handleModalSuccess = () => {
        setModal('');
        setNewSearch(true);
    };

    const handleNewSearch = () => {
        setSelectedDoctor(doctor);
        setSelectedOffice(office);
        setSelectedPatient(patient);
        setSelectedSpecialty(specialty);
        setNewSearch(true);
    };

    const handleRangeChange = (start, end) => {
        setSelectedRange({
            end: end.format('YYYY-MM-DD'),
            start: start.format('YYYY-MM-DD')
        });
        setNewSearch(true);
    };

    const handleTurnCreate = (selectedDate = moment()) => {
        setModal('create');
        const newDate = selectedDate.isBetween(moment(start), moment(end), 'day', '[]')
            ? selectedDate
            : moment(start);
        setSelectedDate(newDate);
    };

    const handleTurnSelect = selectedTurn => {
        const { resource: { event } } = selectedTurn;
        if (event === 'turn') {
            setModal('edit');
            setSelectedTurn(selectedTurn);
        }
    };

    const sliderOpened = ['create', 'edit'].includes(modal);

    return (
        <Container>
            <ExternalTurnModal
                active={ externalTurnModal }
                onToggle={ () => setExternalTurnModal(false) }
            />
            <TurnDownloadModal
                active={ downloadActive }
                onClose={ () => setDownloadActive(false) }
            />
            <TurnSlider
                active={ sliderOpened }
                agenda={ availableAgenda }
                calendarData={ selectedTurn }
                doctor={ selectedDoctor }
                initialDate={ selectedDate }
                mode={ modal }
                office={ selectedOffice }
                specialty={ selectedSpecialty }
                turnId={ selectedTurn?.resource?.id }
                turnList={ turns.filter(({ resource: { event } }) => event === 'turn') }
                onCancel={ () => setModal('') }
                onSuccess={ handleModalSuccess }
            />
            <FilterContainer>
                <Filters active={ showFilters }>
                    <Row>
                        <Col xs="12">
                            <Row>
                                <FilterCol md="6" xs="12">
                                    <SpecialtyPicker
                                        { ...specialtyInput }
                                        allowAll
                                        objectChange
                                        disabled={ sliderOpened }
                                        hasError={ !!searchErrors?.specialty }
                                        margin="0px 0px 10px 0px"
                                        params={{ context: 'appointment' }}
                                        value={ specialty?.id }
                                    />
                                    <ErrorMessage relative error={ searchErrors?.specialty } marginBottom={ 0 } marginLeft={ 0 } />
                                </FilterCol>
                                <FilterCol md="6" xs="12">
                                    <DoctorPicker
                                        { ...doctorInput }
                                        allowNone
                                        objectChange
                                        disabled={ sliderOpened }
                                        fontSize="11px"
                                        hasError={ !!searchErrors?.doctor }
                                        specialty={ specialty?.id }
                                        value={ doctor.id }
                                    />
                                    <ErrorMessage relative error={ searchErrors?.doctor } marginBottom={ 0 } marginLeft={ 0 } />
                                </FilterCol>
                                <FilterCol md="6" xs="12">
                                    <OfficePicker
                                        { ...officeInput }
                                        objectChange
                                        disabled={ sliderOpened }
                                        hasError={ !!searchErrors.office }
                                        params={ doctor?.id }
                                        value={ office?.id }
                                    />
                                    <ErrorMessage relative error={ searchErrors.office } marginBottom={ 0 } marginLeft={ 0 } />
                                </FilterCol>
                                <FilterCol md="6" xs="12">
                                    <PatientPicker
                                        { ...patientInput }
                                        allowNone
                                        objectChange
                                        disabled={ sliderOpened }
                                        fontSize="11px"
                                        value={ patient?.id }
                                    />
                                </FilterCol>
                            </Row>
                        </Col>
                    </Row>
                    <ButtonFooter justify="flex-start" margin={ !xs ? '10px 0px 10px 25px' : '10px 0px 10px 15px' }>
                        <ExpandButton
                            caption={ t('Buscar') }
                            disabled={ sliderOpened }
                            loading={ loading }
                            size={ !xs ? 90 : 70 }
                            onClick={ handleNewSearch }
                        />
                        <ExpandButton
                            disabled={ loading || sliderOpened }
                            fontSize={ 24 }
                            icon="get_app"
                            size={ !xs ? 90 : 70 }
                            onClick={ () => setDownloadActive(true) }
                        />
                        {
                            isSuperadmin(email) &&
                            <ExpandButton
                                caption={ t('Externo') }
                                disabled={ loading || sliderOpened }
                                size={ !xs ? 90 : 70 }
                                onClick={ () => setExternalTurnModal(true) }
                            />
                        }
                        <SelectSearch
                            { ...forceStepInput }
                            margin="5px 0px 0px 0px"
                            options={ [{
                                label: t('Intervalo automático'),
                                value: undefined
                            }, ...[10, 15, 30, 60].map(e => ({
                                label: `${ e } ${ t('min') }`,
                                value: e
                            }))
                            ]
                            }
                            width="200px"
                        />
                    </ButtonFooter>
                </Filters>
                <OnlyXS>
                    <ButtonFooter margin="10px 0px">
                        <IconButton
                            disabled={ sliderOpened }
                            icon={ showFilters ? 'expand_less' : 'expand_more' }
                            label={ `${ showFilters ? t('Ocultar') : t('Mostrar') } ${ t('filtros') }` }
                            size={ 20 }
                            onClick={ toggleFilters }
                        />
                    </ButtonFooter>
                </OnlyXS>
            </FilterContainer>
            {
                !isEmpty(selectedDoctor) && !isEmpty(selectedOffice) && !!selectedDoctor?.id &&
                <ButtonFooter justify="flex-start" margin="0px 0px 10px 0px">
                    <ExpandButton
                        caption={ t('Nuevo turno') }
                        disabled={ sliderOpened }
                        icon="add"
                        size={ 60 }
                        width={ 140 }
                        onClick={ () => handleTurnCreate() }
                    />
                    <DoctorMessage>
                        { t('Profesional') }: { selectedDoctor.name } { t('en') } { selectedOffice.name }.
                        { !isEmpty(selectedPatient) ? ` ${ t('Paciente') }: ${ selectedPatient.last_name }, ${ selectedPatient.first_name }.` : '' }
                    </DoctorMessage>
                </ButtonFooter>
            }
            {
                (!isEmpty(selectedDoctor) || !isEmpty(selectedSpecialty)) && !isEmpty(selectedOffice) &&
                <CalendarContainer>
                    <CalendarWrapper
                        agenda={ availableAgenda }
                        disabled={ sliderOpened }
                        events={ turns }
                        forceStep={ forceStep }
                        loading={ loading }
                        onRangeChange={ handleRangeChange }
                        onSelectEvent={ handleTurnSelect }
                        onSelectSlot={ ({ start }) => handleTurnCreate(moment(start)) }
                    />
                </CalendarContainer>
            }
        </Container>
    );
};

export default TurnListing;
